'use client';

import { Banner } from '@repo/ui/components/ui/banner';
import { Button } from '@repo/ui/components/ui/button';
import { Card } from '@repo/ui/components/ui/card';
import { Empty } from '@repo/ui/components/ui/empty';
import { toast } from '@repo/ui/components/ui/sonner';
import { WORDING } from '@web/constants/wording';

import {
  Heart,
  Map,
  MapPin,
  PawPrint,
  TentTree,
} from '@repo/ui/components/ui/icons';
import { ReviewBar } from '@repo/ui/components/ui/review-bar';
import { Tabs, TabsList, TabsTrigger } from '@repo/ui/components/ui/tabs';
import { BusinessService } from '@web/components/business-detail/business-service';
import { BusinessServiceTypeSection } from '@web/components/business-detail/business-service-type-section';
import { GridImageViewer } from '@web/components/grid-image-viewer';
import { ImageViewer } from '@web/components/image-viewer';
import { SkeletonBusinessDetailLoading } from '@web/components/skeleton-loading';
import { DEFAULT_IMAGE_PATH } from '@web/constants/general';
import { useBusinessDetail } from '@web/hooks/useBusinessDetail';
import { businessLogic, calculateRating } from '@web/lib/business-logic';
import { handelUndefined } from '@web/utils/handelUndefined';
import _ from 'lodash';
import Link from 'next/link';
import { notFound, usePathname } from 'next/navigation';
import { Fragment } from 'react';
import 'react-photo-view/dist/react-photo-view.css';
import { IBusiness, IBusiness_Address } from '@web/types/business';
import dynamic from 'next/dynamic';
import { useAuthStore } from '@web/lib/store/auth-store';
import { BtnSpinner } from '@web/components/spinner';
import { useMutation, UseQueryResult } from '@tanstack/react-query';
import { IServiceBE } from '@web/types/services';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  SubmitHandler,
  useForm,
} from '@repo/ui/components/ui/form';
import { Input } from '@repo/ui/components/ui/input';
import { pawsomeLogic } from '@web/lib/pawsome-logic';

const BuMap = dynamic(() => import('@web/components/leaftlet-map'), {
  ssr: false,
});

export default function BusinessDetail({ id }: { id: string }) {
  const {
    filterState,
    approvedStatusMute,
    business,
    raw,
    serviceQuery,
    onUpdateSelectedFilter,
  } = useBusinessDetail(id);
  const pathName = usePathname();
  const isAdminPath = pathName.includes('admin');
  const { data, isLoading } = business;
  const businessInfo = data?.businessInfo;
  const businessService = data?.businessService;
  const { isAdmin } = useAuthStore();
  const isAdminRole = isAdmin && isAdminPath;
  if (isLoading)
    return (
      <div className="container space-y-4 pt-8">
        <SkeletonBusinessDetailLoading />
      </div>
    );
  if (!businessInfo) {
    notFound();
  }

  if (!businessService) {
    notFound();
  }

  const images = businessInfo.business_image.map((image) =>
    image.url ? businessLogic.getImageUrl(image.url) : DEFAULT_IMAGE_PATH,
  );

  const joinAddress = `${businessInfo?.business_address?.province} ${businessInfo?.business_address?.district} ${businessInfo?.business_address?.sub_district} `;

  const businessPawsome = businessInfo?.business_pawsome;
  const expertiseRating = businessPawsome?.expertise ?? 0;
  const comfortRating = businessPawsome?.comfort ?? 0;
  const valueRating = businessPawsome?.value ?? 0;
  const luxuryRating = businessPawsome?.luxury ?? 0;
  const cleanlinessRating = businessPawsome?.cleanliness ?? 0;
  const reviews = businessPawsome?.reviews ?? [];

  const review_bar: {
    id: string;
    title: string;
    score: number;
  }[] = [
    {
      id: 'expertise',
      title: 'Expertise - Skills & Knowledge',
      score: expertiseRating,
    },
    {
      id: 'value',
      title: 'Value - Worth Every Money',
      score: valueRating,
    },
    {
      id: 'luxury',
      title: 'Luxury - High End Features',
      score: luxuryRating,
    },
    {
      id: 'comfort',
      title: 'Comfort - Easy & Enjoyable',
      score: comfortRating,
    },
    {
      id: 'cleanliness',
      title: 'Cleanliness - Spotless & Tidy',
      score: cleanlinessRating,
    },
  ] as const;

  const overallRating = calculateRating(
    expertiseRating,
    comfortRating,
    valueRating,
    luxuryRating,
    cleanlinessRating,
  );

  const location =
    (businessInfo.business_address?.location as IBusiness_Address['location'])
      ?.coordinates || null;

  const onApprove = async (status: 'verified' | null) => {
    try {
      await approvedStatusMute.mutateAsync({ id, status });
      await business.refetch();
      toast.success(WORDING.APPROVE_BUSINESS_SUCCESS);
    } catch (error) {
      toast.error(WORDING.APPROVE_BUSINESS_FAILED);
    }
  };

  return (
    <>
      {isAdminRole && businessInfo.verify_status !== 'verified' ? (
        <Button
          className="fixed right-[2%] top-[110px] z-[9999]"
          onClick={() => onApprove('verified')}
          disabled={approvedStatusMute.isPending}
        >
          <BtnSpinner isLoading={approvedStatusMute.isPending} />
          Approve
        </Button>
      ) : null}
      {isAdminRole && businessInfo.verify_status === 'verified' ? (
        <Button
          className="fixed right-[2%] top-[110px] z-[9999]"
          onClick={() => onApprove(null)}
          disabled={approvedStatusMute.isPending}
        >
          <BtnSpinner isLoading={approvedStatusMute.isPending} />
          Un Approve
        </Button>
      ) : null}

      <div className="container space-y-4 pt-8">
        <GridImageViewer urls={images} />
        <div className="flex flex-wrap gap-14 pt-10 lg:flex-nowrap">
          <div className="w-full space-y-8 lg:w-2/3">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-1">
                <h1 className="text-h4 font-bold">
                  {businessInfo.business_name}
                </h1>
                <div>
                  <p className="text-muted-foreground icon-wrapper text-base">
                    <MapPin size={16} /> {handelUndefined(joinAddress)}
                  </p>
                </div>
              </div>
              <div>
                {businessInfo.business_address &&
                businessInfo.business_address.map_url ? (
                  <div>
                    <Link
                      href={businessInfo.business_address?.map_url}
                      target="_blank"
                    >
                      <Button variant={'outline'}>
                        <Map size={16} /> Google Map
                      </Button>
                    </Link>
                  </div>
                ) : null}
                {location ? (
                  <div className="mt-6">
                    <BuMap
                      currentPos={location}
                      onClick={() => {
                        return;
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <hr />
            <div className="flex flex-col gap-8">
              <div className="space-y-8">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-h6">Our Services</p>
                  {/* Pet type */}
                  <Tabs
                    defaultValue={filterState.selectedPetType}
                    value={filterState.selectedPetType}
                    onValueChange={(val) =>
                      onUpdateSelectedFilter({
                        ...filterState,
                        selectedPetType: val,
                      })
                    }
                  >
                    <TabsList>
                      {raw.pet_type.map(({ value, label, icon }) => {
                        return (
                          <TabsTrigger key={value} value={value}>
                            <span className="mr-1">{icon}</span>
                            {label}
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </Tabs>
                </div>
                <div className="space-y-4">
                  <div className="flex w-full space-x-7 overflow-x-auto">
                    {/* Service Type */}
                    {raw.serviceType.map(({ id, name, icon }) => {
                      return (
                        <button
                          onClick={() => {
                            onUpdateSelectedFilter({
                              ...filterState,
                              selectedServiceType: id,
                              ...(raw.serviceCategory?.length === 0 && {
                                selectedServiceCategory: null,
                              }),
                              ...(raw.serviceCategory?.length === 0 && {
                                isOtherServiceCategory: true,
                              }),
                            });
                          }}
                          key={id}
                        >
                          <BusinessServiceTypeSection
                            id={id}
                            icon={icon}
                            service_type_name={name}
                            selected={
                              filterState.selectedServiceType
                                ? filterState.selectedServiceType === id
                                : name === 'All Services'
                            }
                          />
                        </button>
                      );
                    })}
                  </div>
                  {!_.isEmpty(serviceQuery.data) ? (
                    <div className="space-y-2">
                      <div className="text-md text-muted-foreground font-medium">
                        Category
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            onUpdateSelectedFilter({
                              ...filterState,
                              selectedServiceCategory: null,
                              isOtherServiceCategory: true,
                            });
                          }}
                          className={`${filterState.isOtherServiceCategory || raw.serviceCategory?.length === 0 ? 'border-foreground text-foreground bg-background border' : 'text-muted-foreground bg-muted hover:opacity-70'} w-fit cursor-pointer space-y-1 rounded-lg px-4 py-2 font-semibold`}
                        >
                          <div>อื่นๆ</div>
                        </Button>
                        {raw.serviceCategory?.length >= 1 ? (
                          <div className="flex gap-2 overflow-x-auto">
                            {raw.serviceCategory?.map(({ id, name }) => {
                              return (
                                <Button
                                  key={id}
                                  onClick={() => {
                                    onUpdateSelectedFilter({
                                      ...filterState,
                                      selectedServiceCategory: id,
                                      isOtherServiceCategory: false,
                                    });
                                  }}
                                  className={`${!filterState.isOtherServiceCategory && filterState.selectedServiceCategory === id ? 'border-foreground text-foreground bg-background border' : 'text-muted-foreground bg-muted hover:opacity-70'} w-fit cursor-pointer space-y-1 rounded-lg px-4 py-2 font-semibold`}
                                >
                                  <div>{name}</div>
                                </Button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <Service
                serviceQuery={serviceQuery}
                businessInfo={businessInfo as unknown as IBusiness}
              />
            </div>
            <hr />
            <div>
              <p className="text-h6">Overview</p>
              <br />
              <div className="business-detail-overview text-muted-foreground text-base">
                {businessInfo?.description ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: businessInfo.description,
                    }}
                  />
                ) : (
                  <Empty icon={<PawPrint size={48} strokeWidth={1.5} />}>
                    "Thanks for your paws here! The information is almost here.
                    Woof woof!"
                  </Empty>
                )}
              </div>
            </div>
            <hr />
            <div className="flex gap-3 py-4">
              <ImageViewer urls={['']} width={40} height={40} />
              <div>
                <p>Hosted by {businessInfo.contact_info?.full_name}</p>
                <p className="text-muted-foreground">
                  Joined in{' '}
                  {businessInfo.contact_info?.created_at.split('T')[0]}
                </p>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/3">
            {isAdminRole ? (
              <Card className="sticky top-24 space-y-5 px-6 py-8">
                <PawsomeEditor buId={businessInfo.id} review_bar={review_bar} />
              </Card>
            ) : (
              <Card className="sticky top-24 space-y-5 px-6 py-8">
                <div className="text-center">
                  <Rating score={+overallRating.toFixed(0)} />
                  <p className="text-b2">Pawsome!</p>
                  <p className="text-h2">{overallRating}</p>
                  <p className="text-b3 text-muted-foreground">
                    Petcation Overall Rating
                  </p>
                </div>
                <div className="space-y-2">
                  {review_bar.map((item) => (
                    <ReviewBar
                      key={item.title}
                      title={item.title}
                      score={item.score as 0 | 1 | 2 | 3 | 4 | 5}
                    />
                  ))}
                </div>
                {reviews.length > 0 &&
                  reviews.map((review: string, index: number) => {
                    return (
                      <Banner
                        key={index}
                        variant={'info'}
                        size={'sm'}
                        width={'full'}
                      >
                        <p>{review}</p>
                        <div className="w-fit">
                          <Heart size={24} strokeWidth={1.5} />
                        </div>
                      </Banner>
                    );
                  })}
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const Service = ({
  serviceQuery,
  businessInfo,
}: {
  serviceQuery: UseQueryResult<IServiceBE[], Error>;
  businessInfo: IBusiness;
}) => {
  if (serviceQuery.isLoading) {
    return (
      <Card className="h-[157px] p-4">
        <div className="flex animate-pulse space-x-4">
          <div className="flex-1 space-y-6 py-1">
            <div className="bg-muted h-5 rounded"></div>
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-muted col-span-2 h-5 rounded"></div>
                <div className="bg-muted col-span-1 h-5 rounded"></div>
              </div>
              <div className="bg-muted h-5 rounded"></div>
            </div>
          </div>
        </div>
      </Card>
    );
  }
  if (serviceQuery.isError) {
    return <div></div>;
  }
  if (!_.isEmpty(serviceQuery.data)) {
    return serviceQuery.data!.map(
      ({
        id,
        detail,
        service_name,
        pet_type,
        service_about,
        service_variant,
      }) => (
        <BusinessService
          key={id}
          detail={detail ?? ''}
          service_name={service_name}
          service_about={service_about ?? ''}
          service_variant={service_variant.map(
            ({ id, variant_name, price, status }) => ({
              id: id ?? '',
              variant_name,
              price,
              status,
            }),
          )}
          pet_type={pet_type}
          line_url={
            (
              businessInfo?.metadata as {
                [key: string]: string | undefined;
              }
            )?.line_id
          }
        />
      ),
    );
  }

  return (
    <Empty icon={<TentTree size={48} strokeWidth={1.5} />}>
      Mark your calendars! The Sevies are launching soon! We can't wait to make
      your pet's life Pawsome!
    </Empty>
  );
};

const Rating = ({ score }: { score: number }) => {
  if (score <= 0) return null;

  return (
    <p>
      {Array.from(Array(score)).map((_, index) => (
        <Fragment key={index}>⭐️</Fragment>
      ))}
    </p>
  );
};

type IPawsomeForm = {
  id: string;
  [key: string]: any;
};

const PawsomeEditor = ({
  buId,
  review_bar,
}: {
  buId: string;
  review_bar: {
    id: string;
    title: string;
    score: number;
  }[];
}) => {
  const form = useForm<IPawsomeForm>();
  const updateMute = useMutation({
    onMutate: async (data: {
      id: string;
      cleanliness: number;
      comfort: number;
      expertise: number;
      luxury: number;
      value: number;
    }) => {
      await pawsomeLogic.updatePawsome(data.id, {
        cleanliness: data.cleanliness,
        comfort: data.comfort,
        expertise: data.expertise,
        luxury: data.luxury,
        value: data.value,
      });
    },
  });
  const onSubmit: SubmitHandler<IPawsomeForm> = (data: IPawsomeForm) => {
    try {
      updateMute.mutate({
        id: buId,
        cleanliness: data.cleanliness ?? 0,
        comfort: data.comfort ?? 0,
        expertise: data.expertise ?? 0,
        luxury: data.luxury ?? 0,
        value: data.value ?? 0,
      });
      toast.success(WORDING.UPDATE_BUSINESS_INFO_SUCCESS);
    } catch (error) {
      console.error('error', error);
      toast.error(WORDING.UPDATE_BUSINESS_INFO_FAILED);
    }
  };
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {review_bar.map((item) => {
          return (
            <div className="mt-3">
              <FormLabel className="w-ful">
                <div>{item.title}</div>
              </FormLabel>
              <FormField
                control={form.control}
                name={item.id as unknown as string}
                defaultValue={item.score}
                render={({ field }) => (
                  <FormItem className="mt-2 w-full ">
                    <FormControl>
                      <Input {...field} type="number" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          );
        })}
        <Button
          type="submit"
          variant="default"
          className="mt-3"
          disabled={updateMute.isPending}
        >
          <BtnSpinner isLoading={updateMute.isPending} /> Submit
        </Button>
      </form>
    </Form>
  );
};
